// src/modules/profile/store/chatStore.js
import {defineStore} from 'pinia';
import {AUTH_TOKEN_KEY} from "@/constants.js";
import api from "@/core/services/axios.js";
import notifier from "@/core/utils/notifier.js";
import {useAuthStore} from "@/store/authStore.js";
import {useNavigation} from "@/router/navigation.js";
import {getErrorMessage, BAN_ERRORS} from "@/core/utils/errorMapper.js";

// Feature flag: отключение чата на фронтенде.
// Читает из Vite env `VITE_CHAT_ENABLED` при сборке, по умолчанию выключено.
const _rawChatEnabled = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_CHAT_ENABLED : undefined;
export const CHAT_ENABLED = (function() {
    if (typeof _rawChatEnabled === 'string') {
        const v = _rawChatEnabled.toLowerCase().trim();
        return ['1','true','yes','on'].includes(v);
    }
    return false;
})();

export const useChatStore = defineStore('chatState', {
    state: () => ({
        messages: [],
        paginationCursor: {},
        currentUser: null,
        currentChat: null,
        ws: null,
        chats: [],
        wsState: 'disconnected',

        loading: false,
        allLoaded: false,
        initialized: false,
        wsReconnectTimeout: null,
        // Счётчик для триггера скролла вниз из store без доступа к DOM
        scrollToBottomTrigger: 0,
    }),
    actions: {
        setCurrentUser(user) {
            this.currentUser = user;
        },
        setCurrentChat(chat) {
            this.currentChat = chat;
        },
        clear() {
            this.messages = [];
            this.paginationCursor = {};
            this.currentChat = null;
            this.loading = false;
            this.allLoaded = false;
        },
        getLogoChat(chat = {}) {
            const normalizeAvatarPath = (avatarPath) => {
                if (!avatarPath) return null;
                if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://') || avatarPath.startsWith('/')) {
                    return avatarPath;
                }
                return `/uploads_public/${avatarPath}`;
            };

            if (chat?.partner_avatar && chat?.partner_avatar !== '') {
                return normalizeAvatarPath(chat.partner_avatar)
            }

            return new URL('@/assets/images/chat_default_logo.svg', import.meta.url).href;
        },
        /**
         * Пометить сообщения как прочитанные
         * @param {string|Date} messageIdOrDate - ID сообщения или дата, до которой помечать сообщения
         */
        async markMessagesAsRead(messageIdOrDate) {
            if (!CHAT_ENABLED) {
                console.warn('[markMessagesAsRead] Skipped: chat feature disabled');
                return;
            }
            if (!this.currentChat?.id || !messageIdOrDate) {
                console.warn('[markMessagesAsRead] Skip: no chatId or messageIdOrDate', { 
                    chatId: this.currentChat?.id, 
                    messageIdOrDate 
                });
                return;
            }

            try {
                // Определяем тип параметра
                const isDate = messageIdOrDate instanceof Date ||
                              (!isNaN(Date.parse(messageIdOrDate)) && isNaN(Number(messageIdOrDate)));

                if (isDate) {
                    // Если передана дата - помечаем все сообщения до этой даты
                    const date = messageIdOrDate instanceof Date ? messageIdOrDate : new Date(messageIdOrDate);

                    // Находим последнее сообщение ОТ ДРУГОГО пользователя до этой даты
                    const messagesBeforeDate = this.messages.filter(msg =>
                        String(msg.sender_id) !== String(this.currentUser?.id) &&
                        !msg.is_read &&
                        new Date(msg.created_at) <= date
                    );

                    if (messagesBeforeDate.length === 0) {
                        return;
                    }

                    // Берем самое новое сообщение для отправки на бэкенд
                    const lastMessage = messagesBeforeDate[messagesBeforeDate.length - 1];
                    const messageId = lastMessage.id;

                    // Бэкенд требует chat_id и message_id в URL
                    await api.patch(`/chats/${this.currentChat.id}/messages/${messageId}/read`);

                    // Обновляем локальное состояние - помечаем все сообщения до этой даты
                    this.messages.forEach(msg => {
                        if (String(msg.sender_id) !== String(this.currentUser?.id) &&
                            new Date(msg.created_at) <= date) {
                            msg.is_read = true;
                        }
                    });
                } else {
                    // Если передан ID - помечаем конкретное сообщение
                    const messageId = messageIdOrDate;

                    // Бэкенд требует chat_id и message_id в URL
                    const response = await api.patch(`/chats/${this.currentChat.id}/messages/${messageId}/read`);

                    // Обновляем локальное состояние СРАЗУ после успешного запроса
                    // Помечаем сообщение как прочитанное
                    const message = this.messages.find(m => m.id === messageId);
                    if (message) {
                        message.is_read = true;
                    }

                    // Помечаем все сообщения ОТ СОБЕСЕДНИКА до этого момента как прочитанные
                    const messageTime = new Date(message?.created_at);
                    this.messages.forEach(msg => {
                        if (String(msg.sender_id) !== String(this.currentUser?.id) &&
                            new Date(msg.created_at) <= messageTime) {
                            msg.is_read = true;
                        }
                    });
                }
            } catch (error) {
                if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                    return;
                }
            }
        },
        async fetchMessages(chatId) {
            if (!CHAT_ENABLED) {
                console.warn('[fetchMessages] Skipped: chat feature disabled');
                return;
            }
            try {
                if (this.loading || this.allLoaded) return;
                this.loading = true;

                let url = `/chats/${chatId}/messages?limit=20`; 
                if (this.paginationCursor?.last_time && this.paginationCursor?.last_id) {
                    url += `&last-time=${this.paginationCursor.last_time}&last-id=${this.paginationCursor.last_id}`;
                }

                const response = await api.get(url);
                const {data, next_cursor, has_more} = response.data;

                if (!data || data.length === 0) {
                    this.allLoaded = true;
                } else {
                    // Бэкенд возвращает от новых к старым.
                    // Чтобы вверху списка были САМЫЕ старые, нам нужно развернуть полученную порцию
                    const olderMessages = [...data].reverse();

                    // Проверяем на дубликаты перед добавлением
                    const existingIds = new Set(this.messages.map(m => m.id));
                    const newUniqueMessages = olderMessages.filter(m => !existingIds.has(m.id));

                    // Добавляем их в начало общего списка
                    this.messages = [...newUniqueMessages, ...this.messages];

                    this.paginationCursor = next_cursor || {};
                    this.allLoaded = !has_more;
                }
            } catch (error) {
                console.error("Ошибка загрузки сообщений", error);
            } finally {
                this.loading = false;
            }
        },
        async selectChat(chatId) {
            if (!CHAT_ENABLED) {
                console.warn('[selectChat] Skipped: chat feature disabled');
                return;
            }
            this.clear();
            const chat = this.chats.find(c => c.id === chatId);
            if (!chat) {
                console.error('Chat not found:', chatId);
                return;
            }

            const userId = useAuthStore().userId;
            if (userId) {
                this.setCurrentUser({id: String(userId)});
            }

            // Бэкенд возвращает: id, listing_id, listing_title, partner_id, partner_username, partner_avatar
            this.setCurrentChat({
                id: chatId,
                name: chat.partner_username,
                listing_id: chat.listing_id,
                listing_title: chat.listing_title,
                listing_type: chat.listing_type,
                quick_post_id: chat.quick_post_id,
                partner_id: chat.partner_id,
                partner_username: chat.partner_username,
                partner_avatar: chat.partner_avatar,
                last_message: chat.last_message_text,
                last_message_time: chat.last_message_time,
                // Важно для проверки прав на бэкенде
                buyer_id: chat.buyer_id,
                seller_id: chat.seller_id,
            });

            // Сбрасываем счётчик непрочитанных на объекте чата
            if (chat) chat.unread_count = 0;

            this.messages = [];
            await this.fetchMessages(chatId);
        },
        async fetchChats() {
            if (!CHAT_ENABLED) {
                console.warn('[fetchChats] Skipped: chat feature disabled');
                this.chats = [];
                return;
            }
            try {
                const response = await api.get('/chats');
                // Бэкенд возвращает { data: [...], next_cursor, has_more, total_count }
                this.chats = response?.data?.data || [];
            } catch (err) {
                console.error("Error fetching chats:", err);
                return new Error("Не удалось загрузить данные.");
            }
        },
        async sendMessage(content) {
            if (!CHAT_ENABLED) {
                console.warn('[sendMessage] Skipped: chat feature disabled');
                return;
            }

            if (!this.currentUser || !this.currentChat) {
                console.error('Cannot send message: missing currentUser or currentChat');
                return;
            }

            try {
                // Бэкенд требует listing_id и text
                // Если чат еще не создан (нет chat_id), передаем recipient_id
                const payload = {
                    listing_id: this.currentChat.listing_id,
                    text: content,
                };

                // Если chat_id существует, передаем его
                if (this.currentChat.id) {
                    payload.chat_id = this.currentChat.id;
                } else {
                    // Если чата еще нет, передаем recipient_id для создания нового чата
                    payload.recipient_id = this.currentChat.partner_id;
                }

                const response = await api.post('/chats/messages', payload);

                // Бэкенд возвращает { chat_id, message_id, created_at }
                const newMessage = {
                    id: response?.data?.message_id,
                    chat_id: response?.data?.chat_id || this.currentChat.id,
                    sender_id: String(this.currentUser.id), // Приводим к строке для корректного сравнения
                    text: content,
                    is_read: false,
                    created_at: response?.data?.created_at,
                };

                this.messages.push(newMessage);

                // Обновляем последнее сообщение в списке чатов
                const chat = this.chats.find(c => c.id === newMessage.chat_id);
                if (chat) {
                    chat.last_message_text = content;
                    chat.last_message_time = newMessage.created_at;
                }

                // Если chat_id был nil, обновляем currentChat с новым chat_id
                if (!this.currentChat.id && response?.data?.chat_id) {
                    this.currentChat.id = response.data.chat_id;
                }
            } catch (error) {
                console.error("Error sending message:", error);
                throw error;
            }
        },
        async connectWebSocket() {
            if (typeof window === 'undefined') return

            try {
                const access_token = localStorage.getItem(AUTH_TOKEN_KEY);
                if (!access_token) {
                    console.warn("[WS] Отмена: токен не найден");
                    return;
                }

                const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';

                const getWsUrl = (token) => {
                    if (!apiBaseUrl) {
                        const protocol = window.location.protocol === "https:" ? "wss://" : "ws://";
                        return `${protocol}${window.location.host}/api/v1/ws?token=${token}`;
                    }
                    const wsBase = apiBaseUrl.replace(/^http/, 'ws');
                    return `${wsBase.replace(/\/$/, '')}/api/v1/ws?token=${token}`;
                };

                const wsUrl = getWsUrl(access_token);

                this.wsState = 'connecting';

                if (this.ws) {
                    this.disconnectWebSocket();
                }

                this.ws = new WebSocket(wsUrl);

                this.ws.onopen = () => {
                    this.wsState = 'connected';
                };

                this.ws.onerror = (err) => {
                    console.warn("[connectWebSocket] WebSocket error:", err);
                    this.wsState = 'error';
                    this.scheduleReconnect();
                };

                this.ws.onclose = (event) => {
                    this.wsState = 'disconnected';

                    // 1008 = ClosePolicyViolation — сервер закрыл соединение из-за
                    // отзыва доступа (бан / потеря подтверждения email), а не
                    // сетевого сбоя. Переподключение тем же токеном упрётся в тот
                    // же отказ на хендшейке — не долбим им сервер. event.reason несёт
                    // код причины (см. closeConnection в internal/transport/ws).
                    if (event.code === 1008) {
                        if (BAN_ERRORS.has(event.reason)) {
                            this.disconnectWebSocket();
                            const {routes, navigateTo} = useNavigation();
                            useAuthStore().logout().then(() => navigateTo(routes.LOGIN));
                            notifier.error('ws-access-revoked', getErrorMessage(event.reason, 'Ваш аккаунт заблокирован администрацией.'));
                            return;
                        }

                        notifier.error('ws-access-revoked', getErrorMessage(event.reason, 'Доступ к чату ограничён. Обновите страницу или войдите заново'));
                        return;
                    }

                    this.scheduleReconnect();
                };

                this.ws.onmessage = (event) => {
                    try {
                        const message = JSON.parse(event.data);

                        // Debug log

                        // Backend sends different message types:
                        // - type="new_message" for new chat messages
                        // - type="read_message" for message read events
                        // - type="new_notification" for notifications

                        // Мгновенный килл живой сессии при бане (см. StatusSubscriber
                        // бэкенда) — приходит push-сообщением, не дожидаясь следующего
                        // разрыва соединения по 1008.
                        if (message.type === 'access_revoked') {
                            this.disconnectWebSocket();
                            const {routes, navigateTo} = useNavigation();
                            useAuthStore().logout().then(() => navigateTo(routes.LOGIN));
                            notifier.error('ws-access-revoked', getErrorMessage(message.data?.code, 'Ваш аккаунт заблокирован администрацией.'));
                            return;
                        }

                        // Обработка нового сообщения поддержки (бейдж в шапке)
                        if (message.type === 'new_message_support' || message.type === 'support_message') {
                            const supportMsg = message.data?.message || message.data;
                            if (supportMsg?.is_admin) {
                                import('@/modules/support/store/supportStore.js')
                                    .then(({useSupportStore}) => {
                                        const supportStore = useSupportStore();
                                        // WS в supportStore подключён только пока открыта страница поддержки,
                                        // поэтому isViewing корректно только на ней: ушли со страницы — считаем как непрочитанное.
                                        const isViewing = supportStore.ws !== null
                                            && supportStore.selectedTicketId
                                            && String(supportStore.selectedTicketId) === String(supportMsg.ticket_id || supportMsg.chat_id || '');
                                        if (!isViewing) {
                                            supportStore.unreadCount = (supportStore.unreadCount || 0) + 1;
                                        }
                                    })
                                    .catch(err => console.error('supportStore import error:', err));
                            }
                            return;
                        }

                        // Обработка уведомлений
                        if (message.type === 'new_notification') {
                            // Импортируем store динамически, чтобы избежать циклических зависимостей
                            import('@/modules/user_notification/store/userNotificationStore.js')
                                .then(({useUserNotificationStore}) => {
                                    const notificationStore = useUserNotificationStore();

                                    const newNotification = {
                                        id: message.data.id,
                                        title: message.data.title,
                                        description: message.data.description,
                                        is_read: message.data.is_read || false,
                                        created_at: message.data.created_at,
                                        payload: message.data.payload,
                                    };

                                    // Добавляем в начало списка
                                    notificationStore.rows.unshift(newNotification);

                                    // Увеличиваем счетчик непрочитанных
                                    if (!newNotification.is_read) {
                                        notificationStore.unreadCount++;
                                    }

                                    // Quickpost назначает исполнителя без чата: показываем уведомление с контактом и объявлением.
                                    if (newNotification.payload?.action === 'performer_selected' && newNotification.payload?.quick_post_id) {
                                        import('@/modules/quick_post/store/performerSelectionStore.js')
                                            .then(({ usePerformerSelectionStore }) => {
                                                usePerformerSelectionStore().enqueueFromNotification(newNotification);
                                            })
                                            .catch(err => console.error('performerSelectionStore import error:', err));
                                        return;
                                    }

                                    // Показываем toast только если чат НЕ открыт и это НЕ уведомление о сообщении
                                    if (!this.currentChat && newNotification.payload?.type !== 'new_message') {
                                        window.dispatchEvent(new CustomEvent('show-notification-toast', {
                                            detail: {
                                                title: newNotification.title,
                                                message: newNotification.description,
                                                type: 'success',
                                            }
                                        }));
                                    }
                                })
                                .catch(err => console.error('Ошибка импорта notificationStore:', err));
                            return;
                        }

                        // Обработка нового сообщения чата
                        // Бэкенд отправляет: { type: "new_message", data: { chat_id, message: {...} } }
                        if (message.type === 'new_message') {
                            const chatId = message.data?.message?.chat_id;
                            const isCurrentChat = this.currentChat && String(this.currentChat.id) === String(chatId);
                            const isChatOpen = this.currentChat !== null;

                            // Показываем toast только если чат НЕ открыт или сообщение из другого чата
                            if (!isChatOpen || !isCurrentChat) {
                                const senderName = message.data?.message?.sender_username || 'Пользователь';
                                const messageText = message.data?.message?.text || '';
                                window.dispatchEvent(new CustomEvent('show-notification-toast', {
                                    detail: {
                                        title: `Новое сообщение от ${senderName}`,
                                        message: messageText,
                                        type: 'info',
                                    }
                                }));
                            }

                            this.handleNewMessage(message.data);
                            return;
                        }

                        // Обработка прочтения сообщения
                        if (message.type === 'read_message') {
                            this.handleMessageReadByOtherUser(message.data);
                            return;
                        }

                    } catch (error) {
                        console.error('[connectWebSocket] Error parsing WebSocket message:', error);
                    }
                };
            } catch (e) {
                console.error("[connectWebSocket] Failed to connect:", e);
                this.wsState = 'error';
            }
        },

        handleNewMessage(messageData) {
            const message = messageData.message;
            if (!message) {
                console.error('[handleNewMessage] No message data:', messageData);
                return;
            }

            const chatId = message.chat_id;
            const currentUserId = this.currentUser?.id ? String(this.currentUser.id) : null;
            const isFromOther = currentUserId && String(message.sender_id) !== currentUserId;

            const newMessage = {
                id: message.message_id,
                chat_id: chatId,
                sender_id: message.sender_id,
                sender_username: message.sender_username,
                text: message.text,
                is_read: message.is_read,
                created_at: new Date().toISOString(),
            };

            // Обновляем метаданные чата в списке
            const chat = this.chats.find(c => c.id === chatId);
            if (chat) {
                chat.last_message_text = message.text;
                chat.last_message_time = newMessage.created_at;
            }

            if (this.currentChat?.id !== chatId) {
                // Не текущий чат — инкрементируем счётчик непрочитанных прямо на объекте чата
                if (isFromOther && chat) {
                    chat.unread_count = (chat.unread_count || 0) + 1;
                }
                return;
            }

            // Текущий чат — добавляем сообщение и триггерим скролл через store-флаг
            this.messages.push(newMessage);
            this.scrollToBottomTrigger++;

            // Помечаем как прочитанное, если сообщение от собеседника
            if (isFromOther) {
                this.markMessagesAsRead(message.message_id);
            }
        },

        /**
         * Обработчик события прочтения сообщения от WebSocket
         * Бэкенд отправляет это событие тому, кто прочитал сообщение
         * Но нам нужно обновить статус для отправителя сообщения
         */
        handleMessageReadByOtherUser(readData) {
            // Бэкенд отправляет: { chat_id, message_id, is_read, seller_id, buyer_id }
            const chatId = readData?.chat_id;
            const currentUserId = this.currentUser?.id ? String(this.currentUser.id) : null;

            if (!chatId || this.currentChat?.id !== chatId) {
                return;
            }

            // Если текущий пользователь не является отправителем, игнорируем
            // (это событие для того, кто читал, а не для отправителя)
            // Но так как бэкенд отправляет событие ReaderID, а не SenderID,
            // мы просто обновляем локально статус прочтения для наших сообщений
            
            // Обновляем все сообщения от текущего пользователя как прочитанные
            this.messages.forEach(msg => {
                if (currentUserId && String(msg.sender_id) === currentUserId) {
                    msg.is_read = true;
                }
            });
        },

        scheduleReconnect(delay = 10000) {
            if (this.wsReconnectTimeout || this.wsState === 'connected') return;

            console.warn(`Попытка переподключения WebSocket через ${delay / 1000} секунд...`);

            this.wsReconnectTimeout = setTimeout(() => {
                this.wsReconnectTimeout = null;
                this.connectWebSocket();
            }, delay);
        },

        disconnectWebSocket() {
            if (this.ws) {
                // Закрываем с кодом 1000 (нормальное закрытие)
                this.ws.onclose = null;
                this.ws.close(1000, "Normal closure");
                this.ws = null; // Важно обнулить соединение!
                this.wsState = 'disconnected';
            }

            if (this.wsReconnectTimeout) {
                clearTimeout(this.wsReconnectTimeout);
                this.wsReconnectTimeout = null;
            }
        },
        
        async deleteMessage(chatId, messageId) {
            try {
                await api.delete(`/chats/${chatId}/messages/${messageId}`);
                // Удаляем сообщение из локального состояния
                const index = this.messages.findIndex(m => m.id === messageId);
                if (index !== -1) {
                    this.messages.splice(index, 1);
                }
            } catch (error) {
                console.error('Error deleting message:', error);
                throw error;
            }
        },
        
        async deleteChat(chatId) {
            try {
                await api.delete(`/chats/${chatId}`);
                // Удаляем чат из списка
                const index = this.chats.findIndex(c => c.id === chatId);
                if (index !== -1) {
                    this.chats.splice(index, 1);
                }
                // Если это был текущий чат, очищаем
                if (this.currentChat?.id === chatId) {
                    this.clear();
                }
            } catch (error) {
                console.error('Error deleting chat:', error);
                throw error;
            }
        },
    },
});
